import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  Box,
  CircularProgress
} from '@mui/material';
import { Email as EmailIcon, CheckCircle as SuccessIcon } from '@mui/icons-material';
import { trackFormSubmission } from '../utils/analytics';

const ForgotPasswordDialog = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiBase}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email: email.trim().toLowerCase() 
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        trackFormSubmission('forgot_password', true);
      } else {
        setError(data.error || 'An error occurred while processing your request');
        trackFormSubmission('forgot_password', false);
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError('Network error. Please check your connection and try again.');
      trackFormSubmission('forgot_password', false);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess(false);
    setLoading(false);
    onClose();
  };

  const handleBackToLogin = () => {
    handleClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        {success ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <SuccessIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h5" component="h2">
              Check Your Email
            </Typography>
          </Box>
        ) : (
          <Typography variant="h5" component="h2">
            Reset Your Password
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {success ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              If an account with that email exists, we've sent you a password reset link.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please check your email and follow the instructions to reset your password. 
              The link will expire in 1 hour for security reasons.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Didn't receive the email? Check your spam folder or try again.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
                placeholder="Enter your email address"
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        {success ? (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Button 
              onClick={handleBackToLogin}
              variant="contained"
              size="large"
              sx={{ minWidth: 140 }}
            >
              Back to Login
            </Button>
          </Box>
        ) : (
          <Box sx={{ width: '100%', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              onClick={handleClose}
              disabled={loading}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !email.trim()}
              startIcon={loading && <CircularProgress size={20} />}
              sx={{ minWidth: 120 }}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ForgotPasswordDialog;